import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import {POSTER_EDITOR_MOBILE_BOTTOM_BAR_CONTAINER_ID} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.types';
import {updateMobileTimelineHeight} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';
import {useIsCurrentPageVideo} from '@Hooks/poster-editor/useIsCurrentPageVideo';
import useWindowSize from '@Hooks/useWindowSize';
import {MobileTimeline} from './components/mobile-timeline/mobile-timeline';
import {POSTER_MOBILE_TIMELINE_HEIGHT_RATIO, MOBILE_TIMELINE_MIN_HEIGHT, MOBILE_TIMELINE_MAX_HEIGHT} from './poster-editor-mobile-timeline.types';
import styles from './poster-editor-mobile-timeline.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';

export function PosterEditorMobileTimeline(): ReactElement | null {
  const isCurrentPageVideo = useIsCurrentPageVideo();
  const isVersionHistoryModeActive = useAppSelector((state) => {
    return state.posterEditor.isVersionHistoryModeActive;
  });
  const mobileTimelineHeight = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.mobileTimelineHeight;
  });
  const dispatch = useAppDispatch();
  const {windowHeight} = useWindowSize();

  const isResizingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  const handleResizeMove = (e: PointerEvent): void => {
    if (!isResizingRef.current) {
      return;
    }
    const editorWithoutNav = window.posterEditor?.elements[GlobalPosterEditorJqueryElement.EDITOR_WITHOUT_NAV];
    const editorWithoutNavHeight = (editorWithoutNav?.get(0) as HTMLElement | undefined)?.offsetHeight ?? 0;
    const minWhiteboardHeight = 200;
    const dynamicMaxHeight = Math.min(
      MOBILE_TIMELINE_MAX_HEIGHT,
      editorWithoutNavHeight - minWhiteboardHeight
    );

    const deltaY = startYRef.current - e.clientY;
    const newHeight = Math.min(dynamicMaxHeight, Math.max(MOBILE_TIMELINE_MIN_HEIGHT, startHeightRef.current + deltaY));
    dispatch(updateMobileTimelineHeight(newHeight));
  };

  const handleResizeEnd = (): void => {
    isResizingRef.current = false;
    document.body.style.userSelect = '';
    window.removeEventListener('pointermove', handleResizeMove);
    window.removeEventListener('pointerup', handleResizeEnd);
  };

  const handleResizeStart = (e: React.PointerEvent): void => {
    e.preventDefault();
    isResizingRef.current = true;
    startYRef.current = e.clientY;
    startHeightRef.current = mobileTimelineHeight ?? windowHeight * POSTER_MOBILE_TIMELINE_HEIGHT_RATIO;
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', handleResizeMove);
    window.addEventListener('pointerup', handleResizeEnd);
  };

  if (!isCurrentPageVideo || isVersionHistoryModeActive) {
    return null;
  }

  return (
    <div id={POSTER_EDITOR_MOBILE_BOTTOM_BAR_CONTAINER_ID} className={styles.wrapper} style={{minHeight: `${mobileTimelineHeight ?? windowHeight * POSTER_MOBILE_TIMELINE_HEIGHT_RATIO}px`}}>
      <div className={styles.resizeHandle} onPointerDown={handleResizeStart} role="separator" aria-orientation="horizontal" />
      <MobileTimeline />
      <div className={styles.mobileSeekLine} />
    </div>
  );
}
